import { createFileRoute, Link } from "@tanstack/react-router";
import { ShieldCheck, Building2, Users, KeyRound, ArrowRight, Globe, FileSignature, Server } from "lucide-react";
import { FeaturePage } from "@/components/marketing/FeaturePage";
import { alternateLinkList } from "@/lib/i18n/locale";
import hero from "@/assets/nfc-card-trio.jpg";
import sso from "@/assets/ai-abstract.jpg";
import brand from "@/assets/cards-design.webp";
import security from "@/assets/cards-hero.webp";

export const Route = createFileRoute("/vi/solutions/enterprise")({
  head: () => ({
    meta: [
      { title: "Thẻ NFC & CRM cho doanh nghiệp — banana.cards" },
      { name: "description", content: "SSO, SCIM, SOC 2, tính phí theo ghế và đội ngũ thành công riêng. Triển khai thẻ có thương hiệu và CRM AI cho 10.000+ nhân viên trong vài ngày, không phải vài quý." },
      { property: "og:title", content: "Thẻ NFC & CRM cho doanh nghiệp — banana.cards" },
      { property: "og:description", content: "SSO, SCIM, SOC 2, hỗ trợ riêng, SLA 99.9%." },
      { property: "og:image", content: hero },
      { property: "og:locale", content: "vi_VN" },
    ],
    links: alternateLinkList("/solutions/enterprise", "vi"),
  }),
  component: Page,
});

function Page() {
  return (
    <FeaturePage
      eyebrow="Cho doanh nghiệp"
      title="Được xây cho"
      italic="quy mô lớn."
      description="SAML SSO, cấp phát SCIM, audit log, DPA tùy chỉnh và SLA 99.9% — bao quanh chính nền tảng Banana mà nhân viên của bạn đã yêu thích."
      heroImage={hero}
      primaryCta={{ label: "Liên hệ kinh doanh", to: "/vi/contact" }}
      secondaryCta={{ label: "Tải gói bảo mật", to: "/security" }}
      metrics={[
        { v: "10k+", l: "Ghế cấp trong 24h" },
        { v: "99.9%", l: "SLA uptime" },
        { v: "SOC 2", l: "Chứng nhận Type II" },
      ]}
      bullets={[
        { icon: KeyRound, title: "SSO, SCIM & RBAC", desc: "Okta, Azure AD, Google Workspace và mọi IdP SAML 2.0. Cấp phát JIT, vai trò theo nhóm và offboard chỉ một cú nhấp." },
        { icon: Building2, title: "Thương hiệu ở mọi cấp", desc: "Template thẻ toàn tổ chức, thương hiệu phụ theo vùng, tên miền tùy chỉnh, hồ sơ white-label và token thiết kế khóa được cho đội design system." },
        { icon: ShieldCheck, title: "Bảo mật & tuân thủ tích hợp", desc: "SOC 2 Type II, ISO 27001, GDPR, CCPA. DPA tùy chỉnh, data residency tại EU/US/APAC, audit log stream sang SIEM của bạn." },
      ]}
      deepDives={[
        {
          eyebrow: "Định danh",
          title: "Cấp 10.000 thẻ",
          italic: "trong một buổi chiều.",
          description: "Đẩy directory qua SCIM. Banana tạo hồ sơ kỹ thuật số có thương hiệu, gửi thẻ NFC vật lý đến địa chỉ nhà từng nhân viên, và thu hồi truy cập ngay khi HR offboard.",
          points: [
            "SAML 2.0 SSO (Okta, Azure AD, Google, Ping, OneLogin)",
            "Cấp phát JIT SCIM 2.0",
            "Vai trò theo nhóm & phạm vi quyền",
            "Giao thẻ hàng loạt đến nhà toàn thế giới",
          ],
          image: sso,
        },
        {
          eyebrow: "Thương hiệu",
          title: "Thương hiệu trên mỗi",
          italic: "lần chạm, quét và chữ ký.",
          description: "Thiết kế một lần tại HQ, khóa hệ thống, để đội vùng dùng. Template, font, màu, layout và hình ảnh duyệt — được kiểm soát, lập phiên bản và triển khai tức thì.",
          points: [
            "Thư viện template tổ chức & thương hiệu phụ",
            "Tên miền tùy chỉnh & hồ sơ white-label",
            "Hình ảnh duyệt & khóa font",
            "Triển khai chữ ký email cho mọi ghế",
          ],
          image: brand,
          reverse: true,
        },
        {
          eyebrow: "Bảo mật",
          title: "Thứ CISO của bạn",
          italic: "muốn đọc.",
          description: "SOC 2 Type II, ISO 27001, pentest, chương trình công bố lỗ hổng, và mã hóa đầu-cuối trên ghi chú. Chọn data residency tại EU, US hoặc APAC. DPA và BAA tùy chỉnh có sẵn.",
          points: [
            "SOC 2 Type II & ISO 27001",
            "Data residency EU / US / APAC",
            "Stream audit log (Splunk, Datadog, S3)",
            "DPA, BAA & MSA tùy chỉnh có sẵn",
          ],
          image: security,
        },
      ]}
      howItWorks={[
        { title: "Khởi động & xác định phạm vi", desc: "Solutions engineer riêng lập bản đồ IdP, hệ thống thương hiệu, vùng và giai đoạn triển khai." },
        { title: "Pilot trong 2 tuần", desc: "Pilot 100 ghế với SSO đầy đủ, thẻ có thương hiệu được giao và CRM tích hợp hoạt động." },
        { title: "Triển khai toàn cầu", desc: "Cấp phát SCIM + giao thẻ hàng loạt phủ hàng nghìn ghế qua các vùng trong vài ngày." },
        { title: "Thành công lâu dài", desc: "CSM riêng, đánh giá kinh doanh hàng quý, hỗ trợ enterprise 24/7 và đóng góp lộ trình." },
      ]}
      capabilities={[
        "SAML 2.0 SSO & cấp phát SCIM",
        "Phân quyền theo vai trò",
        "SOC 2 Type II & ISO 27001",
        "DPA, BAA & MSA tùy chỉnh",
        "Data residency EU / US / APAC",
        "Stream audit log sang SIEM",
        "Đồng bộ Salesforce, HubSpot & Workday",
        "CSM riêng & hỗ trợ 24/7",
        "SLA uptime 99.9%",
        "Tên miền tùy chỉnh & white-label",
      ]}
      faq={[
        { q: "SSO và SCIM hoạt động như thế nào?", a: "Chúng tôi hỗ trợ mọi nhà cung cấp định danh SAML 2.0 — Okta, Azure AD, Google Workspace, Ping, OneLogin, Duo và nhiều hơn. SCIM 2.0 xử lý cấp phát JIT: người mới được hồ sơ và thẻ giao đến ngày HR thêm họ, và offboard thu hồi truy cập tức thì trên web, mobile và chip NFC vật lý." },
        { q: "Dữ liệu của chúng tôi lưu ở đâu?", a: "Bạn chọn. Banana vận hành các data plane theo vùng tại EU (Frankfurt), US (Virginia) và APAC (Singapore). Toàn bộ dữ liệu — hồ sơ, ghi chú, bản ghi — ở lại vùng bạn chọn. Sao chép xuyên vùng là tùy chọn và mặc định tắt cho ngành quy định." },
        { q: "Bạn nắm những chứng nhận tuân thủ nào?", a: "Tuân thủ SOC 2 Type II, ISO 27001, GDPR và CCPA. Sẵn sàng HIPAA với BAA có chữ ký trên Enterprise. Pentest bên thứ ba hàng năm, chương trình công bố lỗ hổng công khai và trung tâm tin cậy bảo mật với báo cáo hiện hành." },
        { q: "Có thể white-label sản phẩm không?", a: "Có. Tên miền tùy chỉnh, gỡ bỏ thương hiệu Banana, logo và token màu của bạn trên mọi bề mặt — trang hồ sơ, email, pass wallet, khối chữ ký. Gói Enterprise bao gồm app mobile đồng thương hiệu cho triển khai lớn nhất." },
        { q: "Giá ở 10.000 ghế trông như thế nào?", a: "Theo ghế với bậc khối lượng. Bao gồm thẻ không giới hạn (bạn chỉ trả phí vận chuyển & vật liệu cho thay thế vật lý), AI không giới hạn trong fair-use, mọi tích hợp, và thành công riêng. Triển khai 10k ghế điển hình thấp hơn nhiều so với chi phí một quản trị viên CRM full-time." },
        { q: "Có thể go-live nhanh đến mức nào?", a: "Pilot 100 ghế trong 2 tuần. Triển khai đầy đủ 10.000+ ghế thường mất 4–6 tuần đầu-cuối, bao gồm mua sắm, cấu hình SSO, duyệt template thương hiệu và giao thẻ hàng loạt đến địa chỉ nhà toàn thế giới." },
      ]}
      outro={{
        title: "Sẵn sàng khi",
        italic: "bạn sẵn sàng.",
        description: "Nhận demo, gói bảo mật và mô hình giá tùy chỉnh xây quanh số ghế và vùng của bạn.",
      }}
      afterFaq={<EntUseCases />}
    />
  );
}

function EntUseCases() {
  const items = [
    { i: Globe, t: "Triển khai toàn cầu", d: "Data plane đa vùng, giao thẻ hàng loạt đến 80+ quốc gia, hồ sơ nhận biết ngôn ngữ.", to: "/vi/platform" },
    { i: Users, t: "Tổ chức bán hàng quy mô lớn", d: "Template theo đội, bảng quản lý, đồng bộ hai chiều Salesforce & HubSpot.", to: "/vi/solutions/sales" },
    { i: FileSignature, t: "Ngành quy định", d: "HIPAA BAA, audit log, hợp đồng có ký, kiểm soát data residency.", to: "/security" },
    { i: Server, t: "Đội IT & bảo mật", d: "SSO, SCIM, stream SIEM, một trung tâm tin cậy, đầu mối bảo mật được chỉ định.", to: "/security" },
  ];
  return (
    <section className="bg-background">
      <div className="mx-auto max-w-7xl px-4 py-20 md:px-6">
        <p className="text-xs font-bold uppercase tracking-[0.2em] text-muted-foreground">Tình huống doanh nghiệp</p>
        <h2 className="mt-2 font-display text-3xl leading-[1.1] md:text-[2.5rem]">
          Được xây cho cách <em className="italic">tổ chức lớn thực sự mua.</em>
        </h2>
        <div className="mt-10 grid gap-4 md:grid-cols-2">
          {items.map((i) => {
            const Icon = i.i;
            return (
              <Link key={i.t} to={i.to} className="group rounded-2xl bg-muted p-6 transition hover:bg-muted/70">
                <span className="flex h-11 w-11 items-center justify-center rounded-xl bg-brand-soft">
                  <Icon className="h-5 w-5" />
                </span>
                <h3 className="mt-4 text-lg font-bold">{i.t}</h3>
                <p className="mt-2 text-sm leading-relaxed text-muted-foreground">{i.d}</p>
                <span className="mt-4 inline-flex items-center gap-1 text-sm font-bold underline-offset-4 group-hover:underline">
                  Khám phá <ArrowRight className="h-4 w-4" />
                </span>
              </Link>
            );
          })}
        </div>
      </div>
    </section>
  );
}